import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackupSchedulePanel from "../components/BackupSchedulePanel";
import Box from "../components/Box";
import SortableTable from "../components/SortableTable";
import Title from "../components/Title";
import { API_URL } from "../constants/config";
import { useUser } from "../contexts/UserContext";
import DashboardLayout from "../layouts/Dashboard";
import { errorAlert, successAlert } from "../utils/toast";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const formatSize = (bytes) => {
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit++;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const DatabaseBackups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyName, setBusyName] = useState(null);
  const navigate = useNavigate();
  const { user } = useUser();
  const isAdmin = user?.role === "administrator";

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchBackups();
  }, [navigate, isAdmin]);

  const fetchBackups = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/admin/backups`, { headers: authHeaders() })
      .then((response) =>
        setBackups(
          response.data.data.map((backup) => ({
            ...backup,
            type: backup.type === "auto" ? "Automatic" : "Manual",
          }))
        )
      )
      .catch(() => errorAlert("Failed to fetch backups."))
      .finally(() => setLoading(false));
  };

  const createBackup = () => {
    setCreating(true);
    axios
      .post(`${API_URL}/admin/backups`, null, { headers: authHeaders() })
      .then(() => {
        successAlert("Backup created successfully.");
        fetchBackups();
      })
      .catch((error) =>
        errorAlert(error.response?.data?.message || "Backup failed.")
      )
      .finally(() => setCreating(false));
  };

  const downloadBackup = (name) => {
    setBusyName(name);
    axios
      .get(`${API_URL}/admin/backups/${name}/download-link`, {
        headers: authHeaders(),
      })
      .then((response) => {
        // Signed link: the browser downloads it natively, with progress
        window.location.href = response.data.data.url;
      })
      .catch(() => errorAlert("Failed to start the download."))
      .finally(() => setBusyName(null));
  };

  const deleteBackup = (name) => {
    if (!window.confirm(`Delete backup ${name}? This cannot be undone.`)) {
      return;
    }
    setBusyName(name);
    axios
      .delete(`${API_URL}/admin/backups/${name}`, { headers: authHeaders() })
      .then(() => {
        successAlert("Backup deleted.");
        fetchBackups();
      })
      .catch(() => errorAlert("Failed to delete the backup."))
      .finally(() => setBusyName(null));
  };

  const fields = [
    { id: "name", label: "File" },
    { id: "type", label: "Type" },
    { id: "size", label: "Size" },
    { id: "created_at", label: "Created" },
    { id: "actions", label: "Actions" },
  ];

  const formatCell = (row, value, column) => {
    if (column === "size") {
      return formatSize(value);
    }

    if (column === "actions") {
      if (busyName === row.name) {
        return <CircularProgress size={20} />;
      }
      return (
        <>
          <Tooltip title="Download">
            <IconButton onClick={() => downloadBackup(row.name)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => deleteBackup(row.name)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    }

    return value;
  };

  return (
    <DashboardLayout>
      {loading && (
        <div className="relative z-10">
          <div className="preloader-main w-full h-screen bg-white absolute opacity-90">
            <div className="preloader-div flex items-center justify-center flex-col h-full">
              Loading...
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center ">
        <Title title="Database Backups" />
        <button
          className="flex items-center justify-center gap-2 px-4 py-1 text-sm border border-cf--primary text-cf--primary bg-white min-w-[90px] rounded-sm transition-all hover:text-white pt-sans-bold hover:bg-cf--primary mb-4 disabled:opacity-60 disabled:pointer-events-none"
          onClick={createBackup}
          disabled={creating}
        >
          {creating && <CircularProgress size={14} color="inherit" />}
          {creating ? "Creating backup..." : "Create Backup"}
        </button>
      </div>
      {creating && (
        <p className="text-sm mb-4">
          Dumping the database. This can take up to a minute, please keep this
          page open.
        </p>
      )}
      {isAdmin && <BackupSchedulePanel />}
      <Box>
        <div className="w-full">
          <SortableTable
            data={backups}
            fields={fields}
            formatCells={formatCell}
            defaultRowsPerPage={10}
          />
        </div>
      </Box>
    </DashboardLayout>
  );
};

export default DatabaseBackups;
